<template>
	<div
		class="g-block-pay-video-list"
		:class="{
			'g-block-pay-video-list--edit': editable,
			'is-active': editable && active,
			'g-block-pay-video-list--h5': h5,
			'g-block-pay-video-list--compact': compact,
			'g-block-pay-video-list--unlocked': isUnlocked,
		}"
		@click="onWrapClick"
	>
		<div class="g-block-pay-video-list__card">
			<div class="g-block-pay-video-list__header">
				<div class="g-block-pay-video-list__header-main">
					<div class="g-block-pay-video-list__title">{{ listData.title }}</div>
					<div class="g-block-pay-video-list__subtitle">{{ subtitleText }}</div>
				</div>
				<div v-if="!isUnlocked && !shouldHidePrice" class="g-block-pay-video-list__header-action">
					<div class="g-block-pay-video-list__price">
						<span class="g-block-pay-video-list__currency">{{ listData.currency }}</span>
						<span class="g-block-pay-video-list__price-value">{{ listData.price }}</span>
					</div>
					<button
						type="button"
						class="g-block-pay-video-list__unlock-btn"
						:style="buttonStyle"
						@click.stop="onUnlockClick"
					>{{ listData.buttonText }}</button>
				</div>
			</div>

			<div v-if="!isUnlocked && showInfoBar" class="g-block-pay-video-list__info">
				<SoundOutlined class="g-block-pay-video-list__info-icon" />
				<span>
					可免费试看
					<strong>{{ freePreviewCount }}</strong>
					个，解锁后可看全部
					<strong>{{ totalCount }}</strong>
					个视频
				</span>
			</div>

			<div
				class="g-block-pay-video-list__grid"
				:style="{ gap: blockStyle.colGap }"
			>
<div
				v-for="entry in listEntries"
				:key="entry.key"
				class="g-block-pay-video-list__item"
			>
				<div
					class="g-block-pay-video-list__thumb"
					:class="{
						'is-locked': entry.locked,
						'is-playable': isEntryPlayable(entry),
					}"
					:style="thumbStyle"
				>
					<img
						v-if="getCoverUrl(entry.item)"
						:src="getCoverUrl(entry.item)"
						alt=""
					/>
					<i v-else class="mbm-iconfont mbm-shipin"></i>
					<span
						v-if="!entry.locked"
						class="g-block-pay-video-list__play"
						aria-hidden="true"
						role="button"
						aria-label="播放"
						@click.stop="onItemClick(entry, $event)"
					>
						<span class="g-block-pay-video-list__triangle"></span>
					</span>
					<span
						class="g-block-pay-video-list__duration"
						@click.stop="onItemClick(entry, $event)"
					>{{ getDurationText(entry.item) }}</span>
					<div v-if="entry.locked" class="g-block-pay-video-list__lock-mask" :style="{ borderRadius: thumbStyle.borderRadius }">
						<i @click.stop="onUnlockClick" class="mbm-iconfont mbm-suoding g-block-pay-video-list__lock-icon"></i>
					</div>
				</div>
				<div
					class="g-block-pay-video-list__item-title"
					:style="{ color: entry.item.titleColor }"
				>
					{{ entry.item.title }}
				</div>
				<div
					class="g-block-pay-video-list__desc"
					:style="{ color: entry.item.descColor }"
				>
					{{ entry.item.description }}
				</div>
			</div>
			</div>
		</div>

		<video
			v-if="playingUrl"
			ref="playerEl"
			class="g-block-pay-video-list__player"
			:src="playingUrl"
			controls
			playsinline
			@ended="onPlayEnded"
		></video>
		<i v-if="playingUrl" class="mbm-iconfont mbm-guanbi g-block-pay-video-list__close" @click="stopPlay"></i>
	</div>
</template>

<script>
	import baseFunc from '../../../../config/base_func.js';
	import { geditPayUnlockMixin } from '../../../mixins/geditPayUnlock.js';
	import { SoundOutlined } from '@ant-design/icons-vue';
	import {
		getPayVideoListBlockStyle,
		getPayVideoListListEntries,
		getVideoListCoverUrl,
		getVideoListVideoUrl,
		isPayVideoListUnlocked,
		normalizePayVideoListData,
		setPayVideoListUnlocked,
	} from '../../../utils/payVideoList.js';

	export default {
		name: 'GPayVideoListShell',
		mixins: [geditPayUnlockMixin],
		components: { SoundOutlined },
		props: {
			data: {
				type: Object,
				required: true,
			},
			blockId: {
				type: String,
				default: '',
			},
			editable: {
				type: Boolean,
				default: false,
			},
			active: {
				type: Boolean,
				default: false,
			},
			h5: {
				type: Boolean,
				default: false,
			},
			idKey: {
				type: String,
				default: '',
			},
			compact: {
				type: Boolean,
				default: false,
			},
		},
		emits: ['select'],
		data() {
			return {
				unlockedLocal: false,
				playingUrl: '',
				permissionRequests: {},
				currentPlayingEntry: null,
			};
		},
		computed: {
			listData() {
				return normalizePayVideoListData(this.data);
			},
			blockStyle() {
				return getPayVideoListBlockStyle(this.listData, { h5: this.h5 });
			},
			thumbStyle() {
				return {
					borderRadius: this.blockStyle.borderRadius,
				};
			},
			buttonStyle() {
				return {
					background: this.listData.buttonBg,
				};
			},
			totalCount() {
				return this.listData.items.length;
			},
			freePreviewCount() {
				return Math.min(this.listData.freePreviewCount, this.totalCount);
			},
			subtitleText() {
				const subtitle = this.listData.subtitle || '';
				
				return subtitle;
			},
			isUnlocked() {
				if (this.editable) return false;
				return this.unlockedLocal || isPayVideoListUnlocked(this.blockId) || this.isGeditModulePaid;
			},
			listEntries() {
				return getPayVideoListListEntries(this.listData, {
					unlocked: this.isUnlocked,
					compact: this.compact,
				});
			},
			showInfoBar() {
				if (this.compact) return false;
				return this.freePreviewCount > 0 && this.freePreviewCount < this.totalCount;
			},
		},
		watch: {
			blockId: {
				immediate: true,
				handler() {
					this.syncUnlockState();
				},
			},
		},
		beforeUnmount() {
			this.stopPlay();
		},
		methods: {
			syncUnlockState() {
				this.unlockedLocal = isPayVideoListUnlocked(this.blockId);
			},
			getCoverUrl(item) {
				return getVideoListCoverUrl(item);
			},
			getDurationText(item) {
				return item.video?.durationText || '00:00';
			},
			isEntryPlayable(entry) {
				if (!(this.editable || this.h5)) return false;
				return this.editable || !entry.locked;
			},
		onWrapClick() {
			if (this.editable) {
				this.$emit('select');
			}
		},
			onUnlockClick() {
				this.runGeditUnlockFlow({
					applyUnlock: () => {
						setPayVideoListUnlocked(this.blockId, true);
						this.unlockedLocal = true;
					},
				});
			},
			onItemClick(entry, event) {
				event.stopPropagation();
				if (!this.isEntryPlayable(entry)) return;
				const url = getVideoListVideoUrl(entry.item);
				if (!url) return;
				this.currentPlayingEntry = entry;
				const entryKey = entry.key;
				if (this.h5 && entry.item.id && !this.permissionRequests[entryKey]) {
					this.permissionRequests[entryKey] = true;
					this.requestPlayPermission(entry, url).then(() => {
						this.$nextTick(() => {
							this.$refs.playerEl?.play?.();
						});
					}).catch(err => {
						this.permissionRequests[entryKey] = false;
						this.$message.error(err.data?.message || '请求失败');
						this.$refs.playerEl?.pause?.();
					});
				} else {
					this.playingUrl = url;
					this.$nextTick(() => {
						this.$refs.playerEl?.play?.();
					});
				}
			},
			//访问路径中取指定的值
			getQueryStringByChannel(name) {
				const reg = new RegExp('(^|&)' + name + '=([^&]*)(&|$)', 'i')
				if (!window.location.href.split('?')[1]) {
					return null
				}
				const re = window.location.href.split('?')[1].match(reg)
				if (re != null) return unescape(re[2])
				return null
			},
			requestPlayPermission(entry, fallbackUrl) {
				return this.$apiRequest.post('/api/getMediaPlayPermissions', {
					attach_id: entry.item.video.id,
					id_key: this.idKey,
					unique: '',
					app_type:1,
					p_key: this.getQueryStringByChannel('p_key')==null?'':this.getQueryStringByChannel('p_key'),
				}).then(res => {
					if (res.data?.play_url) {
						this.playingUrl = baseFunc.getQiniusign(res.data.play_url);
					} else {
						this.playingUrl = fallbackUrl;
					}
				});
			},
			onPlayEnded() {
				this.stopPlay();
			},
			stopPlay() {
				const el = this.$refs.playerEl;
				if (el) {
					el.pause();
				}
				this.playingUrl = '';
			},
		},
	};
</script>

<style scoped lang="less">
	.g-block-pay-video-list {
		box-sizing: border-box;

		&--edit {
			border: 1px solid transparent;
			border-radius: 4px;
			transition: border-color 0.2s, box-shadow 0.2s;
			cursor: pointer;

			&.is-active {
				border-color: #285af9;
				box-shadow: 0 0 0 1px rgba(40, 90, 249, 0.15);
			}
		}
	}

	.g-block-pay-video-list__card {
		background: #fff;
		border-radius: 12px;
		overflow: hidden;
		box-shadow: 0 0 12px rgba(0, 0, 0, 0.1);
	}

	.g-block-pay-video-list__header {
		display: flex;
		align-items: center;
		justify-content: space-between;
		gap: 12px;
		padding: 14px 10px 10px;
	}

	.g-block-pay-video-list__header-main {
		flex: 1;
		min-width: 0;
	}

	.g-block-pay-video-list__title {
		font-size: 18px;
		font-weight: 700;
		color: #1a1a1a;
		line-height: 1.35;
	}

	.g-block-pay-video-list__subtitle {
		margin-top: 4px;
		font-size: 13px;
		color: #999;
		line-height: 1.4;
	}

	.g-block-pay-video-list__header-action {
		flex-shrink: 0;
		display: flex;
		flex-direction: column;
		align-items: flex-end;
	}

	.g-block-pay-video-list__price {
		display: flex;
		align-items: baseline;
		color: #ff4d4f;
		line-height: 1;
	}

	.g-block-pay-video-list__currency {
		font-size: 14px;
		font-weight: 600;
		margin-right: 2px;
	}

	.g-block-pay-video-list__price-value {
		font-size: 22px;
		font-weight: 700;
	}

	.g-block-pay-video-list__unlock-btn {
		margin-top: 10px;
		margin-bottom: 4px;
		border: none;
		border-radius: 999px;
		padding: 10px 14px;
		font-size: 13px;
		font-weight: normal;
		color: #fff;
		cursor: pointer;
		white-space: nowrap;
		line-height: 1.3;

		&:active {
			opacity: 0.9;
		}
	}

	.g-block-pay-video-list__info {
		display: flex;
		align-items: center;
		gap: 6px;
		margin: 0 10px 10px;
		padding: 8px 10px;
		background: #eef4ff;
		border-radius: 8px;
		font-size: 12px;
		color: #666;
		line-height: 1.5;

		strong {
			color: #285af9;
			font-weight: 600;
		}
	}

	.g-block-pay-video-list__info-icon {
		flex-shrink: 0;
		font-size: 14px;
		color: #285af9;
	}

	.g-block-pay-video-list__grid {
		display: grid;
		grid-template-columns: repeat(2, minmax(0, 1fr));
		padding: 0 10px 14px;
	}

	.g-block-pay-video-list__item {
		min-width: 0;
	}

	.g-block-pay-video-list__thumb {
		position: relative;
		width: 100%;
		aspect-ratio: 16 / 9;
		overflow: hidden;
		background: #f0f0f0;
		display: flex;
		align-items: center;
		justify-content: center;

		&.is-playable {
			cursor: pointer;
		}

		img {
			width: 100%;
			height: 100%;
			object-fit: cover;
			display: block;
		}

		i {
			font-size: 20px;
			color: #fff;
		}
	}

	.g-block-pay-video-list__play {
		position: absolute;
		top: 50%;
		left: 50%;
		z-index: 2;
		display: flex;
		align-items: center;
		justify-content: center;
		width: 40px;
		height: 40px;
		margin: -20px 0 0 -20px;
		border-radius: 50%;
		background: rgba(0, 0, 0, 0.45);
		cursor: pointer;
	}

	.g-block-pay-video-list__triangle {
		display: block;
		width: 0;
		height: 0;
		margin-left: 3px;
		border-top: 7px solid transparent;
		border-bottom: 7px solid transparent;
		border-left: 11px solid #fff;
	}

	.g-block-pay-video-list__duration {
		position: absolute;
		left: 8px;
		bottom: 8px;
		z-index: 2;
		padding: 2px 6px;
		border-radius: 4px;
		background: rgba(0, 0, 0, 0.55);
		color: #fff;
		font-size: 11px;
		line-height: 1.4;
		cursor: pointer;
	}

	.g-block-pay-video-list__lock-mask {
		position: absolute;
		inset: 0;
		z-index: 3;
		display: flex;
		flex-direction: column;
		align-items: center;
		justify-content: center;
		gap: 4px;
		background: rgba(0, 0, 0, 0.15);
	}

	.g-block-pay-video-list__lock-icon {
		font-size: 20px;
		color: #fff;
	}

	.g-block-pay-video-list__lock-text {
		font-size: 9px;
		color: #fff;
		text-shadow: 0 1px 2px rgba(0, 0, 0, 0.3);
	}

	.g-block-pay-video-list__thumb.is-locked img {
		// filter: blur(6px);
		// transform: scale(1.08);
	}

	.g-block-pay-video-list__item-title {
		margin-top: 8px;
		font-size: 14px;
		font-weight: 600;
		line-height: 1.4;
		overflow: hidden;
		text-overflow: ellipsis;
		white-space: nowrap;
	}

	.g-block-pay-video-list__desc {
		font-size: 12px;
		line-height: 1.5;
		overflow: hidden;
		text-overflow: ellipsis;
		white-space: nowrap;
	}

	.g-block-pay-video-list__player {
		position: fixed;
		left: 0;
		top: 0;
		z-index: 9999;
		width: 100%;
		height: 100%;
		object-fit: contain;
		background: #000;
	}

	.g-block-pay-video-list__close {
		position: fixed;
		top: 20px;
		right: 20px;
		width: 40px;
		height: 40px;
		line-height: 40px;
		text-align: center;
		background: rgba(0, 0, 0, 0.5);
		border-radius: 50%;
		color: #fff;
		font-size: 20px;
		cursor: pointer;
		z-index: 10000;
	}

	.g-block-pay-video-list--h5 {
		.g-block-pay-video-list__card {
			border-radius: 0.24rem;
			box-shadow: 0 0 0.24rem rgba(0, 0, 0, 0.1);
		}

		.g-block-pay-video-list__header {
			gap: 0.24rem;
			padding: 0.28rem 0.2rem 0.2rem;
		}

		.g-block-pay-video-list__title {
			font-size: 0.36rem;
		}

		.g-block-pay-video-list__subtitle {
			margin-top: 0.08rem;
			font-size: 0.26rem;
		}

		.g-block-pay-video-list__currency {
			font-size: 0.28rem;
		}

		.g-block-pay-video-list__price-value {
			font-size: 0.44rem;
		}

		.g-block-pay-video-list__unlock-btn {
			padding: 0.2rem 0.28rem;
			font-size: 0.26rem;
			margin-top: 0.2rem;
			margin-bottom: 0.08rem;
		}

		.g-block-pay-video-list__info {
			gap: 0.12rem;
			margin: 0 0.2rem 0.2rem;
			padding: 0.16rem 0.2rem;
			border-radius: 0.16rem;
			font-size: 0.24rem;
		}

		.g-block-pay-video-list__info-icon {
			font-size: 0.28rem;
		}

		.g-block-pay-video-list__grid {
			padding: 0 0.2rem 0.28rem;
		}

		.g-block-pay-video-list__thumb i {
			font-size: 0.4rem;
		}

		.g-block-pay-video-list__play {
			width: 0.8rem;
			height: 0.8rem;
			margin: -0.4rem 0 0 -0.4rem;
		}

		.g-block-pay-video-list__triangle {
			margin-left: 0.06rem;
			border-top-width: 0.14rem;
			border-bottom-width: 0.14rem;
			border-left-width: 0.22rem;
		}

		.g-block-pay-video-list__duration {
			left: 0.16rem;
			bottom: 0.16rem;
			padding: 0.04rem 0.12rem;
			border-radius: 0.08rem;
			font-size: 0.22rem;
		}

		.g-block-pay-video-list__lock-icon {
			font-size: 0.24rem;
		}

		.g-block-pay-video-list__lock-text {
			font-size: 0.18rem;
		}

		.g-block-pay-video-list__lock-mask {
			gap: 0.08rem;
		}

		.g-block-pay-video-list__item-title {
			margin-top: 0.16rem;
			font-size: 0.28rem;
		}

		.g-block-pay-video-list__desc {
			margin-top: 0.08rem;
			font-size: 0.24rem;
		}
	}
</style>
